import { State, Action, StateContext, Selector, StateToken } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { PaginatedResource } from '@shared/models/paginated-resource';
import {
  GeneralLedgerEntry,
  TrialBalanceEntry,
  BalanceSheetEntry,
  IncomeStatementEntry,
  CategoryAccount,
  RegularAccount,
  ReportPeriod,
  AccountMapping,
  AccountTree,
  AssetBudget,
  AssetBudgetResponse,
} from '../models';
import { AccountStateModel } from '@state/account/state';
import { AssetManagementActions } from './asset-management.actions';
import { AssetManagementService } from '../services/asset-management.service';
import { tap } from 'rxjs/operators';
import { CategoryAccountUtils } from '../utils/category-account.utils';
import { ReportPeriodUtils } from '../utils/report-period.utils';
import { GraphqlFilterSort } from '@shared/models/graphql';
import { TrailingIncomeStatement, TrailingIncomeStatementResponse } from '../models/trailing-income-statement';
import { AssetMetadata } from '../models/asset-metadata';

export type AssetManagementFilters = { filter?: { q?: string } };
export type AssetManagementSort = { sort?: GraphqlFilterSort[] };
export type AssetManagementSharedFilters = { report_period?: ReportPeriod };
export type FinancialStatementEntry<T = unknown> = { entries: T[]; report_period: ReportPeriod };

export type PaginatedGeneralLedger = PaginatedResource<GeneralLedgerEntry> &
  AssetManagementFilters &
  AssetManagementSort;
export type PaginatedTrialBalance = PaginatedResource<TrialBalanceEntry> & AssetManagementFilters & AssetManagementSort;
export type PaginatedAccountMapping = PaginatedResource<AccountMapping> & AssetManagementFilters;
export type PaginatedAccountTree = PaginatedResource<AccountTree> & AssetManagementFilters;

export interface AssetManagementStateModel {
  generalLedger?: PaginatedGeneralLedger;
  trialBalance?: PaginatedTrialBalance;
  accountMappings?: PaginatedAccountMapping;
  accountTree?: PaginatedAccountTree;
  balanceSheet?: FinancialStatementEntry<BalanceSheetEntry>;
  incomeStatement?: FinancialStatementEntry<IncomeStatementEntry>;
  categoryAccounts?: CategoryAccount[];
  regularAccounts?: RegularAccount[];
  assetBudget?: AssetBudget;
  trailingIncomeStatement?: TrailingIncomeStatement;
  sharedFilters: AssetManagementSharedFilters;
  asset: AssetMetadata;
}

const initialState = {
  sharedFilters: { report_period: ReportPeriodUtils.getDefaultFullDateReportPeriod() },
  asset: null,
};

@State<AssetManagementStateModel>({
  name: new StateToken<AccountStateModel>('assetManagementState'),
  defaults: {
    ...initialState,
  },
})
@Injectable()
export class AssetManagementState {
  private readonly assetManagementService = inject(AssetManagementService);

  @Selector()
  static asset(state: AssetManagementStateModel) {
    return state?.asset;
  }

  @Selector()
  static generalLedger(state: AssetManagementStateModel) {
    return state?.generalLedger;
  }

  @Selector([AssetManagementState.generalLedger])
  static generalLedgerQFilter(state: AssetManagementStateModel, generalLedger: PaginatedGeneralLedger): string {
    return state?.generalLedger?.filter?.q ?? '';
  }

  @Selector([AssetManagementState.generalLedger])
  static generalLedgerPage(
    state: AssetManagementStateModel,
    generalLedger: PaginatedGeneralLedger,
  ): {
    page: number;
    per_page: number;
  } {
    return {
      page: generalLedger?.page ?? 1,
      per_page: generalLedger?.per_page ?? 20,
    };
  }

  @Selector([AssetManagementState.trialBalance])
  static trialBalancePage(
    state: AssetManagementStateModel,
    trialBalance: PaginatedTrialBalance,
  ): {
    page: number;
    per_page: number;
  } {
    return {
      page: trialBalance?.page ?? 1,
      per_page: trialBalance?.per_page ?? 20,
    };
  }

  @Selector()
  static trialBalance(state: AssetManagementStateModel) {
    return state?.trialBalance;
  }

  @Selector()
  static balanceSheet(state: AssetManagementStateModel): BalanceSheetEntry[] {
    return state?.balanceSheet?.entries;
  }

  @Selector()
  static incomeStatement(state: AssetManagementStateModel): IncomeStatementEntry[] {
    return state?.incomeStatement?.entries;
  }
  @Selector()
  static balanceSheetPeriod(state: AssetManagementStateModel): ReportPeriod {
    return state?.balanceSheet?.report_period;
  }

  @Selector()
  static incomeStatementPeriod(state: AssetManagementStateModel): ReportPeriod {
    return state?.incomeStatement?.report_period;
  }

  @Selector()
  static regularAccounts(state: AssetManagementStateModel) {
    return state?.regularAccounts;
  }

  @Selector([AssetManagementState.regularAccounts])
  static regularAccountsOptions(state: AssetManagementStateModel, regularAccounts: RegularAccount[]) {
    return regularAccounts?.map((account) => ({
      label: account.gl_account_name,
      value: account.id,
    }));
  }

  @Selector()
  static categoryAccounts(state: AssetManagementStateModel) {
    return state?.categoryAccounts;
  }

  @Selector([AssetManagementState.categoryAccounts])
  static categoryAccountOptions(state: AssetManagementStateModel, categoryAccounts: CategoryAccount[]) {
    return categoryAccounts ? CategoryAccountUtils.generateCategoryAccountOptions(categoryAccounts) : null;
  }

  @Selector()
  static accountMapping(state: AssetManagementStateModel) {
    return state?.accountMappings;
  }

  @Selector([AssetManagementState.accountMapping])
  static accountMappingIndexedByAccountId(state: AssetManagementStateModel, accountMappings: PaginatedAccountMapping) {
    // TODO: maybe this shouldn't be paginated...
    const accountMappingsResult = accountMappings?.results ?? [];
    const accountMappingHash = {};

    accountMappingsResult.forEach((accountMapping) => {
      if (!accountMappingHash[accountMapping.gl_account.id]) {
        accountMappingHash[accountMapping.gl_account.id] = [accountMapping];
      } else {
        accountMappingHash[accountMapping.gl_account.id].push(accountMapping);
      }
    });

    return accountMappingHash;
  }

  @Selector([AssetManagementState.accountMappingIndexedByAccountId, AssetManagementState.regularAccounts])
  static unmappedAccounts(
    state: AssetManagementStateModel,
    accountMappingIndexedByAccountId: { [accountId: number]: PaginatedAccountMapping['results'] },
    regularAccounts: RegularAccount[],
  ) {
    return regularAccounts.filter((account) => !accountMappingIndexedByAccountId[account.id]);
  }

  @Selector()
  static accountTree(state: AssetManagementStateModel): PaginatedAccountTree {
    return state?.accountTree;
  }

  @Selector()
  static sharedReportPeriodFilter(state: AssetManagementStateModel): ReportPeriod {
    return state.sharedFilters.report_period;
  }

  @Selector()
  static assetBudget(state: AssetManagementStateModel): AssetBudget {
    return state.assetBudget;
  }

  @Selector([AssetManagementState.assetBudget])
  static assetBudgetReportPeriod(state: AssetManagementStateModel, assetBudget: AssetBudget): ReportPeriod {
    return assetBudget?.report_period;
  }

  @Selector()
  static trailingIncomeStatement(state: AssetManagementStateModel): TrailingIncomeStatement {
    return state.trailingIncomeStatement;
  }

  @Selector([AssetManagementState.trailingIncomeStatement])
  static trailingIncomeStatementPeriod(
    state: AssetManagementStateModel,
    trailingIncomeStatement: TrailingIncomeStatement,
  ): ReportPeriod {
    return trailingIncomeStatement?.report_period;
  }

  @Action(AssetManagementActions.LoadGeneralLedger)
  loadGeneralLedger(
    ctx: StateContext<AssetManagementStateModel>,
    { accountingReportParams }: AssetManagementActions.LoadGeneralLedger,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.getGeneralLedger(accountingReportParams, asset).pipe(
      tap((generalLedger) =>
        ctx.setState({
          ...ctx.getState(),
          generalLedger: {
            ...generalLedger,
            filter: { q: accountingReportParams.q },
            sort: accountingReportParams.sort,
          },
          sharedFilters: { report_period: { ...accountingReportParams.report_period } },
        }),
      ),
    );
  }

  @Action(AssetManagementActions.ImportGeneralLedger)
  importGeneralLedger(
    ctx: StateContext<AssetManagementStateModel>,
    { file, source }: AssetManagementActions.ImportGeneralLedger,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.importGeneralLedger(file, asset, source);
  }

  @Action(AssetManagementActions.LoadTrialBalance)
  loadTrialBalance(
    ctx: StateContext<AssetManagementStateModel>,
    { accountingReportParams }: AssetManagementActions.LoadTrialBalance,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService
      .getTrialBalance(
        {
          ...accountingReportParams,
        },
        asset,
      )
      .pipe(
        tap((trialBalance) => {
          ctx.setState({
            ...ctx.getState(),
            trialBalance: {
              ...trialBalance,
              sort: accountingReportParams?.sort,
            },
            sharedFilters: {
              report_period: accountingReportParams.report_period,
            },
          });
        }),
      );
  }

  @Action(AssetManagementActions.LoadBalanceSheet, { cancelUncompleted: true })
  loadBalanceSheet(
    ctx: StateContext<AssetManagementStateModel>,
    { reportPeriod }: AssetManagementActions.LoadBalanceSheet,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.getBalanceSheet(reportPeriod, asset).pipe(
      tap((balanceSheet) =>
        ctx.setState({
          ...ctx.getState(),
          balanceSheet: { entries: balanceSheet, report_period: reportPeriod },
          sharedFilters: {
            report_period: reportPeriod,
          },
        }),
      ),
    );
  }

  @Action(AssetManagementActions.LoadIncomeStatement, { cancelUncompleted: true })
  loadIncomeStatement(
    ctx: StateContext<AssetManagementStateModel>,
    { reportPeriod }: AssetManagementActions.LoadIncomeStatement,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.getIncomeStatement(reportPeriod, asset).pipe(
      tap((incomeStatement) =>
        ctx.setState({
          ...ctx.getState(),
          incomeStatement: { entries: incomeStatement, report_period: reportPeriod },
          sharedFilters: {
            report_period: reportPeriod,
          },
        }),
      ),
    );
  }

  @Action(AssetManagementActions.LoadRegularAccounts)
  loadRegularAccounts(ctx: StateContext<AssetManagementStateModel>) {
    return this.assetManagementService.getRegularAccounts().pipe(
      tap((regularAccountSettings) =>
        ctx.setState({
          ...ctx.getState(),
          regularAccounts: regularAccountSettings,
        }),
      ),
    );
  }

  @Action(AssetManagementActions.CreateAccountMapping)
  createAccountMapping(
    ctx: StateContext<AssetManagementStateModel>,
    { accountTreeId, accountMappingRequest }: AssetManagementActions.CreateAccountMapping,
  ) {
    return this.assetManagementService.createAccountMapping(accountTreeId, accountMappingRequest);
  }

  @Action(AssetManagementActions.UpdateAccountMapping)
  updateAccountMapping(
    ctx: StateContext<AssetManagementStateModel>,
    { accountTreeId, accountMappingRequest }: AssetManagementActions.UpdateAccountMapping,
  ) {
    return this.assetManagementService.updateAccountMapping(accountTreeId, accountMappingRequest);
  }

  @Action(AssetManagementActions.LoadAccountMappings)
  loadAccountMappings(
    ctx: StateContext<AssetManagementStateModel>,
    { accountingReportParams, accountTreeId }: AssetManagementActions.LoadAccountMappings,
  ) {
    return this.assetManagementService
      .getAccountMappings(accountTreeId, accountingReportParams)
      .pipe(tap((accountMappings) => ctx.setState({ ...ctx.getState(), accountMappings: accountMappings })));
  }

  @Action(AssetManagementActions.DeleteAccountMapping)
  deleteAccountMapping(
    ctx: StateContext<AssetManagementStateModel>,
    { accountTreeId, accountMapping }: AssetManagementActions.DeleteAccountMapping,
  ) {
    return this.assetManagementService.deleteAccountMapping(accountTreeId, accountMapping);
  }

  @Action(AssetManagementActions.LoadCategoryAccounts)
  loadCategoryAccounts(ctx: StateContext<AssetManagementStateModel>) {
    return this.assetManagementService.getCategoryAccounts().pipe(
      tap((categoryAccounts) =>
        ctx.setState({
          ...ctx.getState(),
          categoryAccounts,
        }),
      ),
    );
  }

  @Action(AssetManagementActions.CreateCategoryAccount)
  createCategoryAccount(
    ctx: StateContext<AssetManagementStateModel>,
    { categoryAccountRequest }: AssetManagementActions.CreateCategoryAccount,
  ) {
    return this.assetManagementService.createCategoryAccount(categoryAccountRequest);
  }

  @Action(AssetManagementActions.UpdateCategoryAccount)
  updateCategoryAccount(
    ctx: StateContext<AssetManagementStateModel>,
    { categoryAccountRequest }: AssetManagementActions.UpdateCategoryAccount,
  ) {
    return this.assetManagementService.updateCategoryAccount(categoryAccountRequest);
  }

  @Action(AssetManagementActions.DeleteCategoryAccount)
  deleteCategoryAccount(
    ctx: StateContext<AssetManagementStateModel>,
    { categoryAccountRequest }: AssetManagementActions.DeleteCategoryAccount,
  ) {
    return this.assetManagementService.deleteCategoryAccount(categoryAccountRequest);
  }

  @Action(AssetManagementActions.UpdateCategoryAccounts)
  updateCategoryAccounts(
    ctx: StateContext<AssetManagementStateModel>,
    { categoryAccounts }: AssetManagementActions.UpdateCategoryAccounts,
  ) {
    return this.assetManagementService.updateCategoryAccounts(categoryAccounts);
  }

  @Action(AssetManagementActions.ImportChartOfAccounts)
  importChartOfAccounts(
    ctx: StateContext<AssetManagementStateModel>,
    { accountTreeId, file }: AssetManagementActions.ImportChartOfAccounts,
  ) {
    return this.assetManagementService.importChartOfAccounts(accountTreeId, file);
  }

  @Action(AssetManagementActions.LoadAccountTrees)
  loadAccountTrees(ctx: StateContext<AssetManagementStateModel>) {
    return this.assetManagementService.getAccountTrees().pipe(
      tap((accountTree) =>
        ctx.setState({
          ...ctx.getState(),
          accountTree,
        }),
      ),
    );
  }

  @Action(AssetManagementActions.LoadAssetBudget)
  loadAssetBudget(
    ctx: StateContext<AssetManagementStateModel>,
    { reportPeriod }: AssetManagementActions.LoadAssetBudget,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.getAssetBudget(asset, reportPeriod).pipe(
      tap(({ report_period_range, results }: AssetBudgetResponse) => {
        ctx.setState({
          ...ctx.getState(),
          assetBudget: {
            report_period: reportPeriod,
            report_period_range,
            budget: results,
          },
          sharedFilters: {
            report_period: reportPeriod,
          },
        });
      }),
    );
  }

  @Action(AssetManagementActions.LoadTrailingIncomeStatement)
  loadTrailingIncomeStatement(
    ctx: StateContext<AssetManagementStateModel>,
    { reportPeriod }: AssetManagementActions.LoadTrailingIncomeStatement,
  ) {
    const { asset } = ctx.getState();

    return this.assetManagementService.getTrailingIncomeStatement(asset, reportPeriod).pipe(
      tap(({ report_period_range, actual_periods, results }: TrailingIncomeStatementResponse) =>
        ctx.setState({
          ...ctx.getState(),
          trailingIncomeStatement: {
            report_period: reportPeriod,
            report_period_range,
            actual_periods,
            trailingIncomeStatement: results,
          },
          sharedFilters: {
            report_period: reportPeriod,
          },
        }),
      ),
    );
  }

  @Action(AssetManagementActions.SetAssetMetadata)
  setAssetMetadata(
    ctx: StateContext<AssetManagementStateModel>,
    { assetMetadata }: AssetManagementActions.SetAssetMetadata,
  ) {
    ctx.setState({ ...ctx.getState(), asset: assetMetadata });
  }

  @Action(AssetManagementActions.Clear)
  clear(ctx: StateContext<AssetManagementStateModel>) {
    ctx.setState({ ...initialState });
  }
}
